import type * as React from 'react';
import { Text, View } from 'react-native';

import * as hiveCore from '@gitbook/hive-core';
import {
    Badge,
    BadgeIconWrapper,
    IntlPluralize,
    ListItem,
    Modal,
    ModalAnimateLifecycle,
    spacing,
    themedFonts,
    useCurrentTheme,
} from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import { Button } from '@gitbook/spine-react';
import { useStateWithDeps } from '@gitbook/util-react-hooks';

import { useContentNavigation } from '../../../Components';

/**
 * Display the modal about conflicts resolution.
 */
export function WithConflictsModal(props: {
    revision: hiveCore.Revision;
}): React.ReactElement | null {
    const { revision } = props;
    const hasConflicts = hiveCore.isConflictRevision(revision);
    const [modal, setModal] = useStateWithDeps<'conflicts' | null>(
        hasConflicts ? 'conflicts' : null,
        [hasConflicts]
    );

    if (!hasConflicts) {
        return null;
    }

    return (
        <ModalAnimateLifecycle>
            {modal === 'conflicts' && hiveCore.isConflictRevision(revision) ? (
                <ConflictsModal
                    revision={revision}
                    onClose={() => {
                        setModal(null);
                    }}
                />
            ) : null}
        </ModalAnimateLifecycle>
    );
}

/**
 * Modal to explain conflicts resolution to the user.
 */
function ConflictsModal(props: {
    revision: hiveCore.ConflictRevision;
    onClose: () => void;
}): React.ReactElement {
    const { revision, onClose } = props;
    const [, font] = useCurrentTheme(themedFonts);
    const { navigateToPage } = useContentNavigation();

    const conflicts = Object.entries(revision.conflicts.pages).filter(
        ([_key, status]) =>
            status === hiveCore.RevisionConflictState.DocumentConflict || status === false
    );

    const unmodifiedBase = Object.entries(revision.conflicts.pages).filter(
        ([_key, status]) =>
            status === hiveCore.RevisionConflictState.DeletedInBaseSubtreeModifiedInHead
    );

    const unmodifiedHead = Object.entries(revision.conflicts.pages).filter(
        ([_key, status]) =>
            status === hiveCore.RevisionConflictState.DeletedInHeadSubtreeModifiedInBase
    );

    const deletedInBase = Object.entries(revision.conflicts.pages).filter(([key, status]) => {
        return (
            status === hiveCore.RevisionConflictState.DeletedInBase &&
            // If page is inside a conflict subtree, we don't show it separately to a user
            !isPageIsInConflictSubtree(revision, key, unmodifiedBase, unmodifiedHead)
        );
    });

    const deletedInHead = Object.entries(revision.conflicts.pages).filter(
        ([key, status]) =>
            status === hiveCore.RevisionConflictState.DeletedInHead &&
            // If page is inside a conflict subtree, we don't show it separately to a user
            !isPageIsInConflictSubtree(revision, key, unmodifiedBase, unmodifiedHead)
    );

    const unmodifiedDescendants = (
        revision: hiveCore.ConflictRevision,
        pageKey: string
    ): hiveCore.RevisionPage[] => {
        const children: hiveCore.RevisionPage[] = hiveCore.getPageDescendantsInRevision(
            revision,
            pageKey
        );
        return children.filter(
            (child) =>
                revision.conflicts.pages[child.id] !==
                    hiveCore.RevisionConflictState.DeletedInHead &&
                revision.conflicts.pages[child.id] !== hiveCore.RevisionConflictState.DeletedInBase
        );
    };

    return (
        <Modal
            title="Conflicts detected"
            subtitle="You’ll need to resolve any conflicts before merging this change request."
            onClose={onClose}
        >
            {conflicts.length ? (
                <View style={[spacing.Margin.bottom400]}>
                    <View style={[spacing.Margin.bottom400]}>
                        <Text style={[font.Label, font.text, font.Bold]}>
                            {conflicts.length} conflicted{' '}
                            <IntlPluralize
                                count={conflicts.length}
                                singular="page"
                                plural="pages"
                            />
                        </Text>
                    </View>
                    <View>
                        {conflicts.map(([pageId, conflictState]) => {
                            const hasBeenResolved = conflictState === false;

                            return (
                                <ListItem
                                    key={pageId}
                                    layout="wide"
                                    icon={
                                        <BadgeIconWrapper>
                                            <Badge
                                                kind={hasBeenResolved ? 'success' : 'merge'}
                                                icon={hasBeenResolved ? Icon.Check : Icon.Conflict}
                                            />
                                        </BadgeIconWrapper>
                                    }
                                    title={revision.pages[pageId].title}
                                    hint={hiveCore.getAbsolutePagePathInRevision(revision, pageId)}
                                    buttons={[
                                        <Button
                                            key="open-cr-button"
                                            label="Open"
                                            kind="secondary"
                                            onPress={() => {
                                                onClose();
                                                navigateToPage(revision, pageId);
                                            }}
                                        />,
                                    ]}
                                />
                            );
                        })}
                    </View>
                </View>
            ) : null}
            {deletedInBase.length ? (
                <>
                    <View style={[spacing.Margin.bottom400]}>
                        <Text style={[font.Label, font.text, font.Bold]}>
                            {deletedInBase.length}{' '}
                            <IntlPluralize
                                count={Object.keys(revision.conflicts.pages).length}
                                singular="page"
                                plural="pages"
                            />{' '}
                            deleted in the primary content but modified in your change request…
                        </Text>
                    </View>
                    {deletedInBase.map(([pageId]) => {
                        return (
                            <ListItem
                                key={pageId}
                                layout="wide"
                                icon={
                                    <BadgeIconWrapper>
                                        <Badge kind="danger" icon={Icon.Trashcan} />
                                    </BadgeIconWrapper>
                                }
                                title={revision.pages[pageId].title}
                                buttons={[
                                    <Button
                                        kind="secondary"
                                        onPress={() => {
                                            onClose();
                                            navigateToPage(revision, pageId);
                                        }}
                                        label="Open"
                                    />,
                                ]}
                            />
                        );
                    })}
                </>
            ) : null}
            {deletedInHead.length ? (
                <>
                    <View style={[spacing.Margin.bottom400]}>
                        <Text style={[font.Label, font.text, font.Bold]}>
                            {deletedInHead.length}{' '}
                            <IntlPluralize
                                count={Object.keys(revision.conflicts.pages).length}
                                singular="page"
                                plural="pages"
                            />{' '}
                            deleted in your change request but modified in the primary content…
                        </Text>
                    </View>
                    {deletedInHead.map(([pageId]) => {
                        return (
                            <ListItem
                                key={pageId}
                                layout="wide"
                                icon={
                                    <BadgeIconWrapper>
                                        <Badge kind="danger" icon={Icon.Trashcan} />
                                    </BadgeIconWrapper>
                                }
                                title={revision.pages[pageId].title}
                                buttons={[
                                    <Button
                                        kind="secondary"
                                        onPress={() => {
                                            onClose();
                                            navigateToPage(revision, pageId);
                                        }}
                                        label="Open"
                                    />,
                                ]}
                            />
                        );
                    })}
                </>
            ) : null}
            {unmodifiedBase.length ? (
                <View style={[spacing.Margin.top400]}>
                    <View style={[spacing.Margin.bottom400]}>
                        <Text style={[font.Label, font.text, font.Bold]}>
                            {unmodifiedBase.length}{' '}
                            <IntlPluralize
                                count={Object.keys(revision.conflicts.pages).length}
                                singular="page"
                                plural="pages"
                            />{' '}
                            deleted pages in primary content with conflicted child page(s)…
                        </Text>
                    </View>
                    {unmodifiedBase.map(([pageId]) => {
                        const descendPages = unmodifiedDescendants(revision, pageId);
                        const title = (
                            <>
                                <b>{revision.pages[pageId].title}</b> and {descendPages.length}{' '}
                                descend{' '}
                                <IntlPluralize
                                    count={descendPages.length}
                                    singular="page"
                                    plural="pages"
                                />
                            </>
                        );

                        return (
                            <ListItem
                                key={pageId}
                                layout="wide"
                                icon={
                                    <BadgeIconWrapper>
                                        <Badge kind="warning" icon={Icon.Conflict} />
                                    </BadgeIconWrapper>
                                }
                                title={title}
                                buttons={[
                                    <Button
                                        kind="secondary"
                                        onPress={() => {
                                            onClose();
                                            navigateToPage(revision, pageId);
                                        }}
                                        label="Open"
                                    />,
                                ]}
                            />
                        );
                    })}
                </View>
            ) : null}
            {unmodifiedHead.length ? (
                <View style={[spacing.Margin.top400]}>
                    <View style={[spacing.Margin.bottom400]}>
                        <Text style={[font.Label, font.text, font.Bold]}>
                            {unmodifiedHead.length}{' '}
                            <IntlPluralize
                                count={Object.keys(revision.conflicts.pages).length}
                                singular="page"
                                plural="pages"
                            />{' '}
                            deleted pages in your change request with conflicted child page(s)…
                        </Text>
                    </View>
                    {unmodifiedHead.map(([pageId]) => {
                        const descendPages = unmodifiedDescendants(revision, pageId);
                        const title = (
                            <>
                                <b>{revision.pages[pageId].title}</b> and {descendPages.length}{' '}
                                descend{' '}
                                <IntlPluralize
                                    count={descendPages.length}
                                    singular="page"
                                    plural="pages"
                                />
                            </>
                        );

                        return (
                            <ListItem
                                key={pageId}
                                layout="wide"
                                icon={
                                    <BadgeIconWrapper>
                                        <Badge kind="warning" icon={Icon.Conflict} />
                                    </BadgeIconWrapper>
                                }
                                title={title}
                                buttons={[
                                    <Button
                                        label="Open"
                                        kind="secondary"
                                        onPress={() => {
                                            onClose();
                                            navigateToPage(revision, pageId);
                                        }}
                                    />,
                                ]}
                            />
                        );
                    })}
                </View>
            ) : null}
        </Modal>
    );
}

/**
 * Checks if the page is insede of the conflicted subtree
 */
function isPageIsInConflictSubtree(
    revision: hiveCore.Revision,
    pageKey: string,
    unmodifiedBase: Array<[string, any]>,
    unmodifiedHead: Array<[string, any]>
) {
    const ancestors = hiveCore.getPageAncestorsInRevision(revision, pageKey);
    const conflictedRoots = [
        ...unmodifiedHead.map(([key]) => key),
        ...unmodifiedBase.map(([key]) => key),
    ];
    return ancestors.find((ancestor) => conflictedRoots.find((root) => root === ancestor.id));
}
